import { useEffect, useState } from 'react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import type { Task, CreateTaskPayload, UpdateTaskPayload, ProjectOption, UserOption, TaskStatus, TaskPriority } from '../types';

const STATUS_OPTIONS = [
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'review', label: 'Review' },
  { value: 'done', label: 'Done' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

interface TaskFormModalProps {
  open: boolean;
  task?: Task | null; // null / undefined = create mode
  projects: ProjectOption[];
  users: UserOption[];
  isAdmin: boolean;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateTaskPayload | UpdateTaskPayload) => void;
}

type FormState = {
  title: string;
  description: string;
  requirements: string;
  project_id: string;
  assigned_to: string[]; // array of user IDs
  status: TaskStatus;
  priority: TaskPriority;
  due_date: string;
  due_time: string;
};

const EMPTY: FormState = {
  title: '',
  description: '',
  requirements: '',
  project_id: '',
  assigned_to: [],
  status: 'todo',
  priority: 'medium',
  due_date: '',
  due_time: '',
};

function taskToForm(task: Task): FormState {
  return {
    title: task.title ?? '',
    description: task.description ?? '',
    requirements: task.requirements ?? '',
    project_id: task.project_id ?? '',
    assigned_to: task.assignees?.map((a) => a.id) ?? [],
    status: task.status ?? 'todo',
    priority: task.priority ?? 'medium',
    due_date: task.due_date ?? '',
    due_time: task.due_time ?? '',
  };
}

export function TaskFormModal({ open, task, projects, users, isAdmin, loading, onClose, onSubmit }: TaskFormModalProps) {
  const isEdit = !!task;
  const [form, setForm] = useState<FormState>(EMPTY);

  useEffect(() => {
    if (open) {
      setForm(task ? taskToForm(task) : EMPTY);
    }
  }, [open, task]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleAssigneeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = Array.from(e.target.selectedOptions).map((o) => o.value).filter(Boolean);
    set('assigned_to', selected);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    const payload: CreateTaskPayload = {
      title: form.title.trim(),
      description: form.description.trim(),
      requirements: form.requirements.trim(),
      project_id: form.project_id || null,
      assigned_to: form.assigned_to,
      status: form.status,
      priority: form.priority,
      due_date: form.due_date || null,
      due_time: form.due_time || undefined,
    };
    onSubmit(payload);
  };

  const projectOptions = [
    { value: '', label: 'No Project' },
    ...projects.map((p) => ({ value: p.id, label: p.name })),
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Task' : 'New Task'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form="task-form" loading={loading}>
            {isEdit ? 'Save Changes' : 'Create Task'}
          </Button>
        </>
      }
    >
      <form id="task-form" onSubmit={handleSubmit} className="space-y-4">
        {/* Title */}
        <Input
          label="Title *"
          name="task-title"
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
          placeholder="Task title…"
          required
        />

        {/* Project + Priority row */}
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Project"
            name="task-project"
            value={form.project_id}
            onChange={(e) => set('project_id', e.target.value)}
            options={projectOptions}
          />
          <Select
            label="Priority"
            name="task-priority"
            value={form.priority}
            onChange={(e) => set('priority', e.target.value as TaskPriority)}
            options={PRIORITY_OPTIONS}
          />
        </div>

        {/* Status + Due date row */}
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Status"
            name="task-status"
            value={form.status}
            onChange={(e) => set('status', e.target.value as TaskStatus)}
            options={STATUS_OPTIONS}
          />
          <Input
            label="Due Date"
            name="task-due"
            type="date"
            value={form.due_date}
            onChange={(e) => set('due_date', e.target.value)}
          />
        </div>

        {/* Assignees — multi-select for admin, or read-only for employee */}
        {isAdmin && (
          <div className="w-full">
            <label htmlFor="task-assignees" className="mb-1.5 block text-body-sm font-medium text-on-surface">
              Assign To <span className="text-on-surface-variant/60">(hold Ctrl/Cmd for multiple)</span>
            </label>
            <select
              id="task-assignees"
              multiple
              value={form.assigned_to}
              onChange={handleAssigneeChange}
              className="h-28 w-full rounded-DEFAULT border border-outline bg-surface px-2 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.display_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Description */}
        <div>
          <label htmlFor="task-desc" className="mb-1.5 block text-body-sm font-medium text-on-surface">
            Description
          </label>
          <textarea
            id="task-desc"
            rows={3}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder="Task details…"
            className="w-full resize-none rounded-DEFAULT border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        {/* Requirements */}
        <div>
          <label htmlFor="task-reqs" className="mb-1.5 block text-body-sm font-medium text-on-surface">
            Requirements
          </label>
          <textarea
            id="task-reqs"
            rows={2}
            value={form.requirements}
            onChange={(e) => set('requirements', e.target.value)}
            placeholder="Acceptance criteria…"
            className="w-full resize-none rounded-DEFAULT border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
      </form>
    </Modal>
  );
}
